import React, { useMemo, useState } from "react";
import { Table, Badge, Form, Row, Col, ProgressBar } from "react-bootstrap";
import { LineChart, Line, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import ExportToExcelButton from "../../common/ExportToExcelButton";
import { TrendIndicator } from "../UsageInsights";
import {
  formatUnits,
  formatUnitsExact,
  formatPercentOfLimit,
  getStatusVariant,
  getStatusLabel,
  progressValue,
  USAGE_STATUS,
} from "../../../utils/usageUtils";

const SORT_OPTIONS = [
  { value: "currentDesc", label: "Current month (high to low)" },
  { value: "percentDesc", label: "Percent of limit (high to low)" },
  { value: "projectedDesc", label: "Projected percent (high to low)" },
  { value: "growthDesc", label: "Growth (fastest first)" },
  { value: "name", label: "Client name" },
];

/** Names the month and value behind a hovered sparkline point. */
function SparklineTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;

  const point = payload[0].payload;
  return (
    <div className="bg-body border rounded shadow-sm px-2 py-1 small">
      <div className="fw-semibold">{point.label}</div>
      <div>{formatUnitsExact(point.usageUnits)} units</div>
    </div>
  );
}

/**
 * Compact usage sparkline across the completed months in the window.
 *
 * Carries a tooltip and a peak marker because the line on its own only shows a SHAPE —
 * a reader can see it spiking and have no way to tell which month that was or how big.
 * Every row spans the same months, so the shapes are comparable down the column.
 */
function Sparkline({ points = [], peakLabel, peakUnits }) {
  const data = useMemo(
    () => (Array.isArray(points) ? points : []).map((p, index) => ({ ...p, index })),
    [points]
  );

  if (!data.length) return null;

  const peakIndex = data.reduce(
    (best, p, i) => (p.usageUnits > data[best].usageUnits ? i : best),
    0
  );

  return (
    <div>
      <div style={{ width: 110, height: 30 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 4, bottom: 2, left: 2, right: 2 }}>
            <YAxis hide domain={[0, "dataMax"]} />
            <Tooltip content={<SparklineTooltip />} cursor={{ stroke: "#adb5bd", strokeWidth: 1 }} />
            <Line
              type="monotone"
              dataKey="usageUnits"
              stroke="#0d6efd"
              strokeWidth={1.5}
              isAnimationActive={false}
              // Only the peak gets a dot, so the eye lands on the month that matters.
              dot={(props) =>
                props.index === peakIndex && data[peakIndex].usageUnits > 0 ? (
                  <circle
                    key={props.index}
                    cx={props.cx}
                    cy={props.cy}
                    r={2.5}
                    fill="#fd7e14"
                    stroke="none"
                  />
                ) : null
              }
              activeDot={{ r: 3 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      {peakLabel && (
        <div className="text-muted" style={{ fontSize: "0.7rem", whiteSpace: "nowrap" }}>
          peak {peakLabel} · {formatUnits(peakUnits)}
        </div>
      )}
    </div>
  );
}

/**
 * One row per client with tier standing, growth and a sparkline.
 *
 * Sorting and filtering happen client-side: the API returns every client unpaginated
 * (roughly 80 of them), so there is no round trip per sort.
 */
function UsageClientsTable({ rows = [], statusFilter, onStatusFilterChange, onSelectClient }) {
  const [sortBy, setSortBy] = useState("currentDesc");
  const [search, setSearch] = useState("");

  const visible = useMemo(() => {
    // Guarded rather than spread directly: anything non-array here throws inside a
    // useMemo and white-screens the whole page, not just this tab.
    let result = Array.isArray(rows) ? [...rows] : [];

    if (statusFilter) {
      result = result.filter((row) => row.status === statusFilter);
    }

    if (search.trim()) {
      const needle = search.trim().toLowerCase();
      result = result.filter(
        (row) =>
          row.clientName?.toLowerCase().includes(needle) ||
          row.clientId?.toLowerCase().includes(needle)
      );
    }

    // Nulls sort last in every numeric ordering — an untiered client has no percentage
    // and should not be treated as 0%, which would park it at the "safe" end.
    const nullsLast = (value) => (value === null || value === undefined ? -Infinity : Number(value));

    switch (sortBy) {
      case "percentDesc":
        result.sort((a, b) => nullsLast(b.percentOfLimit) - nullsLast(a.percentOfLimit));
        break;
      case "projectedDesc":
        result.sort((a, b) => nullsLast(b.projectedPercentOfLimit) - nullsLast(a.projectedPercentOfLimit));
        break;
      case "growthDesc":
        result.sort((a, b) => nullsLast(b.monthOverMonthPercent) - nullsLast(a.monthOverMonthPercent));
        break;
      case "name":
        result.sort((a, b) => (a.clientName || "").localeCompare(b.clientName || ""));
        break;
      default:
        result.sort((a, b) => b.currentMonthUnits - a.currentMonthUnits);
        break;
    }

    return result;
  }, [rows, statusFilter, search, sortBy]);

  const exportRows = useMemo(
    () =>
      visible.map((row) => ({
        "Client Id": row.clientId,
        "Client Name": row.clientName,
        Tier: row.tierName || "",
        "Effective Limit": row.effectiveLimit ?? "",
        "Current Month Units": row.currentMonthUnits,
        "Projected Units": row.projectedUnits,
        "Percent Of Limit": row.percentOfLimit ?? "",
        "Projected Percent Of Limit": row.projectedPercentOfLimit ?? "",
        "3 Month Average": row.trailingThreeMonthAverage,
        "Month Over Month %": row.monthOverMonthPercent ?? "",
        Trend: row.trend,
        "Peak Month": row.peakMonthLabel || "",
        "Peak Month Units": row.peakMonthUnits ?? "",
        Status: row.status,
        "Projected Status": row.projectedStatus,
      })),
    [visible]
  );

  return (
    <>
      <Row className="align-items-end g-2 mb-3">
        <Col md={4}>
          <Form.Group controlId="clientSearch">
            <Form.Label>Search</Form.Label>
            <Form.Control
              type="search"
              placeholder="Client name or id"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group controlId="statusFilter">
            <Form.Label>Status</Form.Label>
            <Form.Select
              value={statusFilter || ""}
              onChange={(e) => onStatusFilterChange?.(e.target.value || null)}
            >
              <option value="">All statuses</option>
              <option value={USAGE_STATUS.OVER}>Over limit</option>
              <option value={USAGE_STATUS.WARN}>Approaching limit</option>
              <option value={USAGE_STATUS.OK}>Within tier</option>
              <option value={USAGE_STATUS.NO_TIER}>No tier assigned</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group controlId="sortBy">
            <Form.Label>Sort by</Form.Label>
            <Form.Select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={2} className="text-md-end">
          <ExportToExcelButton data={exportRows} filename="client-usage" />
        </Col>
      </Row>

      <div className="text-muted small mb-2">
        Showing {visible.length} of {rows.length} clients
      </div>

      <div className="table-responsive">
        <Table striped hover size="sm" className="align-middle mb-0">
          <thead>
            <tr>
              <th className="text-nowrap">Client</th>
              <th className="text-nowrap">Tier</th>
              <th className="text-end text-nowrap">This month</th>
              <th className="text-nowrap" style={{ minWidth: 140 }}>Of limit</th>
              <th className="text-end text-nowrap">Projected</th>
              <th className="text-end text-nowrap" title="Mean of the last 3 completed months">
                3-mo avg
              </th>
              <th
                className="text-end text-nowrap"
                title="Change between the two most recent COMPLETED months. The in-progress month is excluded."
              >
                Prior mo. change
              </th>
              <th
                className="text-center text-nowrap"
                title="Usage across the completed months in the selected period. The in-progress month is excluded."
              >
                Trend
              </th>
              <th className="text-nowrap">Status</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => {
              const variant = getStatusVariant(row.status);

              return (
                <tr
                  key={row.clientId}
                  onClick={() => onSelectClient?.(row.clientId)}
                  style={{ cursor: onSelectClient ? "pointer" : undefined }}
                >
                  <td>
                    <div className="fw-semibold">{row.clientName}</div>
                    <div className="text-muted small">{row.clientId}</div>
                  </td>
                  <td>
                    {row.tierName ? (
                      <>
                        <div>{row.tierName}</div>
                        <div className="text-muted small">{formatUnits(row.effectiveLimit)} units</div>
                      </>
                    ) : (
                      <Badge bg="secondary">None</Badge>
                    )}
                  </td>
                  <td className="text-end" title={formatUnitsExact(row.currentMonthUnits)}>
                    {formatUnits(row.currentMonthUnits)}
                  </td>
                  <td>
                    {row.percentOfLimit === null || row.percentOfLimit === undefined ? (
                      <span className="text-muted">—</span>
                    ) : (
                      <>
                        <ProgressBar
                          now={progressValue(row.percentOfLimit)}
                          variant={variant}
                          style={{ height: 6 }}
                        />
                        <div className="small text-muted mt-1">
                          {formatPercentOfLimit(row.percentOfLimit)}
                        </div>
                      </>
                    )}
                  </td>
                  <td className="text-end">
                    <div>{formatUnits(row.projectedUnits)}</div>
                    <div className={`small text-${getStatusVariant(row.projectedStatus)}`}>
                      {formatPercentOfLimit(row.projectedPercentOfLimit)}
                    </div>
                  </td>
                  <td className="text-end">{formatUnits(row.trailingThreeMonthAverage)}</td>
                  <td className="text-end">
                    {/* Same arrow-plus-magnitude indicator the KPI tiles use, so a number
                        means the same thing wherever it appears in the feature. */}
                    <TrendIndicator value={row.monthOverMonthPercent} />
                  </td>
                  <td className="text-center">
                    <Sparkline
                      points={row.sparkline}
                      peakLabel={row.peakMonthLabel}
                      peakUnits={row.peakMonthUnits}
                    />
                  </td>
                  <td>
                    <Badge bg={variant}>{getStatusLabel(row.status)}</Badge>
                  </td>
                </tr>
              );
            })}
            {visible.length === 0 && (
              <tr>
                <td colSpan={9} className="text-center text-muted py-4">
                  No clients match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </>
  );
}

export default UsageClientsTable;
